import { api } from '@/internationalization/api-client'
import type {
  Billing,
  ExportRequest,
  LanguageSettings,
  NotificationSettings,
  ProfileSettings,
  RecordingSettings,
  RecordingRule,
  SecuritySettings,
  Tenant,
  User,
} from '@/types'

/**
 * Every settings read and write. One file so the endpoint surface for this
 * section is reviewable in one screen — it is also what `npm run check:api`
 * diffs against docs/openapi.yaml.
 */
export const settingsApi = {
  profile: {
    get: () => api.get<ProfileSettings>('/settings/profile'),
    update: (patch: Partial<ProfileSettings>) =>
      api.patch<ProfileSettings>('/settings/profile', patch),
  },
  workspace: {
    get: () => api.get<Tenant>('/settings/workspace'),
    update: (patch: Partial<Tenant>) => api.patch<Tenant>('/settings/workspace', patch),
  },
  members: {
    list: () => api.get<User[]>('/settings/members'),
    invite: (body: { email: string; role: User['role'] }) =>
      api.post<User>('/settings/members/invite', body),
    update: (id: string, patch: { role?: User['role']; status?: User['status'] }) =>
      api.patch<User>(`/settings/members/${id}`, patch),
    remove: (id: string) => api.delete<void>(`/settings/members/${id}`),
  },
  rules: {
    list: () => api.get<RecordingRule[]>('/settings/rules'),
    update: (id: string, patch: Partial<RecordingRule>) =>
      api.patch<RecordingRule>(`/settings/rules/${id}`, patch),
  },
  languages: {
    get: () => api.get<LanguageSettings>('/settings/languages'),
    update: (patch: Partial<LanguageSettings>) =>
      api.patch<LanguageSettings>('/settings/languages', patch),
  },
  notifications: {
    get: () => api.get<NotificationSettings>('/settings/notifications'),
    update: (patch: Partial<NotificationSettings>) =>
      api.patch<NotificationSettings>('/settings/notifications', patch),
  },
  recording: {
    get: () => api.get<RecordingSettings>('/settings/recording'),
    update: (patch: Partial<RecordingSettings>) =>
      api.patch<RecordingSettings>('/settings/recording', patch),
  },
  security: {
    get: () => api.get<SecuritySettings>('/settings/security'),
    update: (patch: Partial<SecuritySettings>) =>
      api.patch<SecuritySettings>('/settings/security', patch),
  },
  billing: () => api.get<Billing>('/settings/billing'),
  requestExport: () => api.post<ExportRequest>('/settings/export'),
}
